import { useEffect, useMemo, useState } from "react";
import {
  AUTH_STATE_CHANGED_EVENT,
  getNormalizedUserRole,
  getStoredAuthSnapshot,
  getStoredBookedOptions,
} from "../utils";
import { JOURNEY_LOG_OPTION_ID } from "../constants";

type AuthState = ReturnType<typeof getStoredAuthSnapshot>;

const isSnapshotEqual = (left: AuthState, right: AuthState) =>
  left.isAuthenticated === right.isAuthenticated && left.userRaw === right.userRaw;

export const useAuthSnapshot = () => {
  const [authSnapshot, setAuthSnapshot] = useState<AuthState>(() => getStoredAuthSnapshot());

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const syncAuthSnapshot = () => {
      setAuthSnapshot((previousSnapshot) => {
        const nextSnapshot = getStoredAuthSnapshot();
        return isSnapshotEqual(previousSnapshot, nextSnapshot)
          ? previousSnapshot
          : nextSnapshot;
      });
    };

    const handleStorageChanged = (event: StorageEvent) => {
      if (!event.key || event.key === "user" || event.key === "isAuthenticated") {
        syncAuthSnapshot();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        syncAuthSnapshot();
      }
    };

    window.addEventListener(AUTH_STATE_CHANGED_EVENT, syncAuthSnapshot);
    window.addEventListener("storage", handleStorageChanged);
    window.addEventListener("focus", syncAuthSnapshot);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener(AUTH_STATE_CHANGED_EVENT, syncAuthSnapshot);
      window.removeEventListener("storage", handleStorageChanged);
      window.removeEventListener("focus", syncAuthSnapshot);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const userData = authSnapshot.userData || {};
  const role = getNormalizedUserRole(userData?.role, userData?.is_admin);
  const isSuperAdmin = role === "super_admin";
  const isCompanyAdmin = role === "company_admin";
  const isAcmiClient = role === "acmi_client";
  const isRefuelEuReader = role === "refuel_eu_reader";
  const canManageUsers = isSuperAdmin || isCompanyAdmin;
  const bookedOptions = getStoredBookedOptions(userData);
  // The server checks the same booking for every role, including super admins.
  const hasJourneyLogAccess =
    !isAcmiClient && !isRefuelEuReader && bookedOptions.includes(JOURNEY_LOG_OPTION_ID);
  const viewAsState =
    userData?.view_as && typeof userData.view_as === "object"
      ? userData.view_as
      : null;
  const superAdminContextState =
    userData?.super_admin_context && typeof userData.super_admin_context === "object"
      ? userData.super_admin_context
      : null;
  const isViewAsActive = Boolean(viewAsState?.active);
  const viewAsOperatorName =
    typeof viewAsState?.operator_name === "string" &&
    viewAsState.operator_name.length
      ? viewAsState.operator_name
      : null;
  const isSuperAdminContextActive = Boolean(superAdminContextState?.active);
  const superAdminContextOperatorName =
    typeof superAdminContextState?.operator_name === "string" &&
    superAdminContextState.operator_name.length
      ? superAdminContextState.operator_name
      : null;
  const canUseViewAs = !isRefuelEuReader && (Boolean(viewAsState?.available) || isSuperAdmin);
  const operatorRef = useMemo(() => {
    const parsed = Number.parseInt(`${userData?.operator_ref ?? ""}`, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }, [userData?.operator_ref]);

  return {
    isAuthenticated: authSnapshot.isAuthenticated,
    userData,
    role,
    isSuperAdmin,
    isCompanyAdmin,
    isAcmiClient,
    isRefuelEuReader,
    canManageUsers,
    bookedOptions,
    hasJourneyLogAccess,
    operatorRef,
    isViewAsActive,
    viewAsOperatorName,
    canUseViewAs,
    viewAsState,
    superAdminContextState,
    isSuperAdminContextActive,
    superAdminContextOperatorName,
  };
};

export default useAuthSnapshot;
